import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Copy, Plus, LogOut, ExternalLink, RefreshCw, Radio, Check, X, Send, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { BuildTimer } from "@/components/BuildTimer";
import { WhatsappInput } from "@/components/WhatsappInput";

const EMPTY = {
  order_number: "",
  customer_whatsapp: "",
  customer_name: "",
  components: {
    cpu: "", gpu: "", motherboard: "",
    ram: "", ram_qty: 1,
    storage: "", storage_qty: 1,
    storage2: "", storage2_qty: 1,
    power_supply: "", case: "", cpu_cooler: "",
    fans: "", fans_qty: 1,
    others: [],
  },
  station_id: "ST01",
};

// Simple components: label + text input, quantity fixed at 1.
const SIMPLE_COMPONENTS = [
  ["cpu", "CPU"], ["gpu", "GPU"], ["motherboard", "Motherboard"],
  ["power_supply", "Power Supply"], ["case", "Case"], ["cpu_cooler", "CPU Cooler"],
];
// Components that carry a quantity alongside the product string.
const QTY_COMPONENTS = [
  ["ram", "RAM", "ram_qty"],
  ["storage", "Storage", "storage_qty"],
  ["storage2", "+ Second Storage", "storage2_qty"],
  ["fans", "Fans", "fans_qty"],
];

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cfStatus, setCfStatus] = useState(null); // { checking, connected, configured, token_ok, live_input_ok, live_input_state }
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [stationOptions, setStationOptions] = useState([]);

  // Selection rules: only WAITING and COMPLETED are eligible. LIVE/PAUSED are
  // hard-locked at both the UI and backend. Server always re-checks.
  const isEligible = (o) => o?.status === "WAITING" || o?.status === "COMPLETED";
  const eligibleIds = orders.filter(isEligible).map((o) => o.id);
  const selectedEligibleIds = eligibleIds.filter((id) => selected.has(id));
  const allEligibleSelected = eligibleIds.length > 0 && selectedEligibleIds.length === eligibleIds.length;

  const toggleOne = (o) => {
    if (!isEligible(o)) return; // hard guard — LIVE/PAUSED cannot be selected
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(o.id) ? n.delete(o.id) : n.add(o.id);
      return n;
    });
  };
  const toggleAllEligible = () => {
    setSelected((prev) => {
      if (allEligibleSelected) {
        const n = new Set(prev);
        eligibleIds.forEach((id) => n.delete(id));
        return n;
      }
      const n = new Set(prev);
      eligibleIds.forEach((id) => n.add(id));
      return n;
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected).filter((id) => eligibleIds.includes(id));
    if (ids.length === 0) return;
    setDeleting(true);
    try {
      const { data } = await client.post("/orders/bulk-delete", { ids });
      const d = (data.deleted || []).length;
      const r = (data.rejected || []).length;
      if (d > 0 && r === 0) toast.success(`${d} build order${d === 1 ? "" : "s"} deleted`);
      else if (d > 0 && r > 0) toast.success(`${d} deleted, ${r} skipped because they are active`);
      else if (d === 0 && r > 0) toast.error(`Skipped ${r} — active builds cannot be deleted`);
      else toast.message("Nothing deleted");
      setSelected((prev) => {
        const n = new Set(prev);
        (data.deleted || []).forEach((id) => n.delete(id));
        return n;
      });
      setConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/orders");
      setOrders(data);
    } catch (e) {
      toast.error("Could not load orders");
    } finally {
      setLoading(false);
    }
  };

  const verifyCloudflare = async () => {
    setCfStatus({ checking: true });
    try {
      const { data } = await client.get("/config/cloudflare/verify");
      setCfStatus(data);
      if (data.connected) toast.success("Cloudflare connected");
      else if (!data.configured) toast.error("Cloudflare env variables not set on this backend");
      else if (!data.token_ok) toast.error("Cloudflare API token invalid");
      else if (!data.live_input_ok) toast.error("STATION 1 Live Input UID invalid");
      else toast.error("Cloudflare not connected");
    } catch (e) {
      setCfStatus({ checking: false, connected: false });
      toast.error(formatApiError(e.response?.data?.detail) || "Verify failed");
    }
  };

  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/stations");
        const active = (data || []).filter((s) => s.active !== false).map((s) => ({ id: s.id, name: s.name, technician_name: s.technician_name }));
        setStationOptions(active);
        // Ensure the form's default station_id is a real, active station.
        if (active.length && !active.find((s) => s.id === form.station_id)) {
          setForm((f) => ({ ...f, station_id: active[0].id }));
        }
      } catch { /* silent — form falls back to EMPTY.station_id */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_whatsapp_parsed?.e164) {
      toast.error("Enter a valid WhatsApp number");
      return;
    }
    setSaving(true);
    try {
      // Clean up components: drop empty OTHERS rows, keep qty as positive int,
      // and only include storage2/fans/qty fields when they have a product string.
      const c = form.components || {};
      const cleaned = {
        cpu: c.cpu || "",
        gpu: c.gpu || "",
        motherboard: c.motherboard || "",
        power_supply: c.power_supply || "",
        case: c.case || "",
        cpu_cooler: c.cpu_cooler || "",
        ram: c.ram || "",
        storage: c.storage || "",
        storage2: c.storage2 || "",
        fans: c.fans || "",
        ram_qty: Math.max(1, parseInt(c.ram_qty, 10) || 1),
        storage_qty: Math.max(1, parseInt(c.storage_qty, 10) || 1),
        storage2_qty: Math.max(1, parseInt(c.storage2_qty, 10) || 1),
        fans_qty: Math.max(1, parseInt(c.fans_qty, 10) || 1),
        others: (c.others || [])
          .map((o) => ({ name: (o.name || "").trim(), qty: Math.max(1, parseInt(o.qty, 10) || 1) }))
          .filter((o) => o.name),
      };
      await client.post("/orders", {
        order_number: form.order_number,
        customer_whatsapp: form.customer_whatsapp_parsed.e164,
        customer_name: form.customer_name,
        components: cleaned,
        station_id: form.station_id,
      });
      toast.success("Order created and link generated");
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create order");
    } finally {
      setSaving(false);
    }
  };

  const customerUrl = (path) => `${window.location.origin}${path}`;

  const copy = async (path) => {
    try {
      await navigator.clipboard.writeText(customerUrl(path));
      toast.success("Customer link copied");
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const resendWhatsapp = async (o) => {
    try {
      const { data } = await client.post(`/orders/${o.id}/whatsapp/resend`);
      if (data.status === "SENT") toast.success(`WhatsApp resent (${data.provider})`);
      else toast.error(`WhatsApp failed: ${data.error || data.status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Resend failed");
    }
  };

  return (
    <div className="min-h-screen">
      {/* Top bar */}
      <div
        className="sticky top-0 z-20 border-b"
        style={{ borderColor: "var(--border)", background: "rgba(5,5,5,0.85)", backdropFilter: "blur(14px)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <BrandMark subtitle="SUPERVISOR" small />
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/50 hidden md:inline">{user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={verifyCloudflare}
              data-testid="verify-cloudflare"
              className="text-white/70 hover:text-white"
              title="Verify Cloudflare Stream connection"
            >
              {cfStatus?.checking ? (
                <RefreshCw size={14} className="mr-1 animate-spin" />
              ) : cfStatus?.connected ? (
                <Check size={14} className="mr-1" style={{ color: "var(--success)" }} />
              ) : cfStatus && !cfStatus.checking ? (
                <X size={14} className="mr-1" style={{ color: "var(--live)" }} />
              ) : (
                <Radio size={14} className="mr-1" />
              )}
              Cloudflare
            </Button>
            <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-orders" className="text-white/70 hover:text-white">
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout" className="text-white/70 hover:text-white">
              <LogOut size={14} className="mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="overline">Command · V1 · Station 1</div>
            <h1 className="font-heading text-4xl md:text-5xl mt-2">Build Orders</h1>
            <p className="text-white/50 mt-2 text-sm">Create an order, generate a secure customer viewing link, hand off to Station 1.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <div className="flex items-center gap-3">
              {selectedEligibleIds.length > 0 && (
                <Button
                  type="button"
                  onClick={() => setConfirmOpen(true)}
                  data-testid="bulk-delete-button"
                  className="h-11 rounded-sm font-medium border"
                  style={{
                    background: "rgba(255,51,51,0.08)",
                    borderColor: "rgba(255,51,51,0.5)",
                    color: "#ff3333",
                  }}
                >
                  <Trash2 size={16} className="mr-2" /> Delete Selected ({selectedEligibleIds.length})
                </Button>
              )}
              <DialogTrigger asChild>
                <Button
                  data-testid="new-order-button"
                  className="h-11 rounded-sm font-medium"
                  style={{ background: "var(--accent)", color: "#000" }}
                >
                  <Plus size={16} className="mr-1" /> New Build Order
                </Button>
              </DialogTrigger>
            </div>
            <DialogContent
              data-testid="new-order-dialog"
              className="max-w-2xl border-white/10 !grid-rows-none !flex !flex-col p-0 gap-0 max-h-[calc(100dvh-2rem)] overflow-hidden"
              style={{ background: "#0a0a0a", color: "#fff" }}
            >
              <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-white/10">
                <DialogTitle className="font-heading text-2xl">New Build Order</DialogTitle>
              </DialogHeader>
              <form
                onSubmit={submit}
                data-testid="new-order-form-scroll"
                className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4 space-y-4"
                style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field
                    label="Order Number"
                    required
                    value={form.order_number}
                    onChange={(v) => setForm({ ...form, order_number: v })}
                    testid="order-number-input"
                  />
                  <WhatsappInput
                    value={form.customer_whatsapp}
                    onChange={({ raw, parsed }) =>
                      setForm((f) => ({
                        ...f,
                        customer_whatsapp: raw,
                        customer_whatsapp_parsed: parsed,
                      }))
                    }
                  />
                  <Field
                    label="Customer Name (optional)"
                    value={form.customer_name}
                    onChange={(v) => setForm({ ...form, customer_name: v })}
                    testid="customer-name-input"
                  />
                  <div>
                    <Label className="overline">Station</Label>
                    <select
                      value={form.station_id}
                      onChange={(e) => setForm({ ...form, station_id: e.target.value })}
                      data-testid="new-order-station-select"
                      className="mt-2 h-10 px-3 rounded-sm border border-white/20 bg-black text-white/90 text-sm w-full focus:outline-none"
                    >
                      {stationOptions.length === 0 && <option value="ST01">STATION 1 (ST01) — NO TECHNICIAN</option>}
                      {stationOptions.map((s) => {
                        const techLabel = (s.technician_name || "").trim().toUpperCase() || "NO TECHNICIAN";
                        return (
                          <option key={s.id} value={s.id} data-testid={`station-option-${s.id}`}>
                            {s.name || s.id} ({s.id}) — {techLabel}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                <div className="pt-2">
                  <div className="overline mb-3">Components</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {SIMPLE_COMPONENTS.map(([k, label]) => (
                      <Field
                        key={k}
                        label={label}
                        value={form.components[k]}
                        onChange={(v) => setForm({ ...form, components: { ...form.components, [k]: v } })}
                        testid={`component-${k}`}
                      />
                    ))}
                    {QTY_COMPONENTS.map(([k, label, qk]) => (
                      <FieldWithQty
                        key={k}
                        label={label}
                        value={form.components[k]}
                        qty={form.components[qk]}
                        onChange={(v) => setForm({ ...form, components: { ...form.components, [k]: v } })}
                        onQtyChange={(q) => setForm({ ...form, components: { ...form.components, [qk]: q } })}
                        testid={`component-${k}`}
                      />
                    ))}
                  </div>
                  <div className="mt-4">
                    <div className="overline mb-2">Others</div>
                    {(form.components.others || []).length === 0 && (
                      <div className="text-white/40 text-xs mb-2">No additional items. Click <b>+ Add Another</b> to include extras (e.g. cables, brackets).</div>
                    )}
                    <div className="space-y-2">
                      {(form.components.others || []).map((o, idx) => (
                        <div key={idx} className="flex items-end gap-2" data-testid={`others-row-${idx}`}>
                          <div className="flex-1">
                            <Field
                              label={`Others ${idx + 1}`}
                              value={o.name}
                              onChange={(v) => {
                                const arr = [...(form.components.others || [])];
                                arr[idx] = { ...arr[idx], name: v };
                                setForm({ ...form, components: { ...form.components, others: arr } });
                              }}
                              testid={`others-name-${idx}`}
                            />
                          </div>
                          <div className="w-24">
                            <QtyInput
                              value={o.qty}
                              onChange={(q) => {
                                const arr = [...(form.components.others || [])];
                                arr[idx] = { ...arr[idx], qty: q };
                                setForm({ ...form, components: { ...form.components, others: arr } });
                              }}
                              testid={`others-qty-${idx}`}
                            />
                          </div>
                          <button
                            type="button"
                            className="h-10 px-2 text-white/40 hover:text-red-400"
                            onClick={() => {
                              const arr = [...(form.components.others || [])];
                              arr.splice(idx, 1);
                              setForm({ ...form, components: { ...form.components, others: arr } });
                            }}
                            data-testid={`others-remove-${idx}`}
                            aria-label="Remove"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.16em] uppercase px-3 py-2 rounded-sm border border-white/20 text-white/80 hover:text-white hover:border-white/40"
                      onClick={() => setForm({
                        ...form,
                        components: {
                          ...form.components,
                          others: [...(form.components.others || []), { name: "", qty: 1 }],
                        },
                      })}
                      data-testid="others-add-another"
                    >
                      <Plus size={13} /> Add Another
                    </button>
                  </div>
                </div>

                <DialogFooter className="pt-2 pb-2">
                  <Button
                    type="submit"
                    disabled={saving}
                    data-testid="submit-order"
                    className="rounded-sm"
                    style={{ background: "var(--accent)", color: "#000" }}
                  >
                    {saving ? "Creating…" : "Create Order & Generate Link"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Orders table */}
        <div
          className="rounded-sm border overflow-hidden"
          style={{ borderColor: "var(--border)", background: "var(--surface)" }}
        >
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/50 w-10">
                  <input
                    type="checkbox"
                    checked={allEligibleSelected}
                    onChange={toggleAllEligible}
                    disabled={eligibleIds.length === 0}
                    aria-label="Select all eligible orders"
                    data-testid="select-all-checkbox"
                    className="w-4 h-4 rounded border-white/30 bg-transparent accent-[color:var(--accent)] cursor-pointer disabled:cursor-not-allowed"
                  />
                </TableHead>
                <TableHead className="text-white/50 overline">Order</TableHead>
                <TableHead className="text-white/50 overline">Customer</TableHead>
                <TableHead className="text-white/50 overline">Station</TableHead>
                <TableHead className="text-white/50 overline">Status</TableHead>
                <TableHead className="text-white/50 overline">Duration</TableHead>
                <TableHead className="text-white/50 overline">WhatsApp</TableHead>
                <TableHead className="text-white/50 overline">Customer Link</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && orders.length === 0 && (
                <TableRow className="border-white/10"><TableCell colSpan={8} className="text-center text-white/40 py-10">Loading orders…</TableCell></TableRow>
              )}
              {!loading && orders.length === 0 && (
                <TableRow className="border-white/10"><TableCell colSpan={8} className="text-center text-white/40 py-10">No orders yet. Click <b>New Build Order</b>.</TableCell></TableRow>
              )}
              {orders.map((o) => (
                <TableRow key={o.id} className="border-white/10 hover:bg-white/5" data-testid={`order-row-${o.order_number}`}>
                  <TableCell className="w-10">
                    <input
                      type="checkbox"
                      checked={selected.has(o.id)}
                      onChange={() => toggleOne(o)}
                      disabled={!isEligible(o)}
                      aria-label={`Select order ${o.order_number}`}
                      data-testid={`select-order-${o.order_number}`}
                      title={isEligible(o) ? "Select for bulk delete" : `Cannot select — order is ${o.status}`}
                      className="w-4 h-4 rounded border-white/30 bg-transparent accent-[color:var(--accent)] cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  </TableCell>
                  <TableCell className="font-mono text-white">{o.order_number}</TableCell>
                  <TableCell className="text-white/80">
                    <div className="flex flex-col leading-tight">
                      <span className="text-white">
                        {o.customer_name || <span className="text-white/40">—</span>}
                      </span>
                      <span className="text-xs text-white/50 font-mono">
                        {o.customer_whatsapp_flag && (
                          <span className="mr-1">{o.customer_whatsapp_flag}</span>
                        )}
                        {o.customer_whatsapp || (o.build_name && (
                          <span className="text-white/40 italic">{o.build_name}</span>
                        )) || ""}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-white/60 font-mono text-xs" data-testid={`row-station-${o.order_number}`}>
                    {(() => {
                      const st = stationOptions.find((s) => s.id === o.station_id);
                      const tech = (st?.technician_name || "").trim().toUpperCase() || "NO TECHNICIAN";
                      return <>{o.station_id} — {tech}</>;
                    })()}
                  </TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell className="text-white/70">
                    <BuildTimer startTime={o.start_time} endTime={o.finish_time} running={o.status === "LIVE"} />
                  </TableCell>
                  <TableCell>
                    <WhatsappCell order={o} onResend={() => resendWhatsapp(o)} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-white/60 font-mono">{o.customer_link_path}</code>
                      <Button variant="ghost" size="icon" onClick={() => copy(o.customer_link_path)} data-testid={`copy-link-${o.order_number}`} className="h-7 w-7 text-white/60 hover:text-white">
                        <Copy size={14} />
                      </Button>
                      <a href={o.customer_link_path} target="_blank" rel="noreferrer" className="text-white/40 hover:text-white" data-testid={`open-link-${o.order_number}`}>
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Bulk delete confirmation modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          className="max-w-md border"
          style={{ background: "#0a0a0a", color: "#fff", borderColor: "rgba(255,51,51,0.35)" }}
          data-testid="bulk-delete-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <AlertTriangle size={20} style={{ color: "#ff3333" }} />
              Delete Selected Build Orders?
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/70 text-sm mt-2 leading-relaxed">
            You are about to permanently delete{" "}
            <span className="text-white font-semibold">{selectedEligibleIds.length}</span>{" "}
            build order{selectedEligibleIds.length === 1 ? "" : "s"} and their associated build-session data.
            This action cannot be undone.
          </p>
          <DialogFooter className="mt-4 gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={deleting}
              data-testid="bulk-delete-cancel"
              className="text-white/70 hover:text-white"
            >
              CANCEL
            </Button>
            <Button
              type="button"
              onClick={bulkDelete}
              disabled={deleting || selectedEligibleIds.length === 0}
              data-testid="bulk-delete-confirm"
              className="rounded-sm font-medium border"
              style={{
                background: "#ff3333",
                borderColor: "#ff3333",
                color: "#fff",
              }}
            >
              <Trash2 size={16} className="mr-2" />
              {deleting ? "DELETING…" : `DELETE ${selectedEligibleIds.length} ORDER${selectedEligibleIds.length === 1 ? "" : "S"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, required, testid }) {
  return (
    <div>
      <Label className="overline">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-2 bg-black border-white/20 text-white h-10"
      />
    </div>
  );
}

function QtyInput({ value, onChange, testid }) {
  return (
    <div>
      <Label className="overline">Qty</Label>
      <Input
        type="number"
        min={1}
        step={1}
        value={value ?? 1}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          onChange(Number.isFinite(n) && n > 0 ? n : 1);
        }}
        data-testid={testid}
        className="mt-2 bg-black border-white/20 text-white h-10 text-center"
      />
    </div>
  );
}

function FieldWithQty({ label, value, qty, onChange, onQtyChange, testid }) {
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1 min-w-0">
        <Field label={label} value={value} onChange={onChange} testid={testid} />
      </div>
      <div className="w-20 shrink-0">
        <QtyInput value={qty} onChange={onQtyChange} testid={`${testid}-qty`} />
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    WAITING: { color: "#a1a1aa", bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.15)" },
    LIVE: { color: "#ff3333", bg: "rgba(255,51,51,0.1)", border: "rgba(255,51,51,0.4)" },
    COMPLETED: { color: "#00ff66", bg: "rgba(0,255,102,0.08)", border: "rgba(0,255,102,0.4)" },
  };
  const s = map[status] || map.WAITING;
  return (
    <Badge
      className="rounded-sm border font-mono text-[10px] tracking-[0.2em]"
      style={{ color: s.color, background: s.bg, borderColor: s.border }}
      data-testid={`status-badge-${status.toLowerCase()}`}
    >
      {status === "LIVE" && <span className="live-dot mr-2" />}
      {status}
    </Badge>
  );
}

function WhatsappCell({ order, onResend }) {
  const s = order.whatsapp_status || "NOT_SENT";
  const map = {
    NOT_SENT: { color: "#71717a", bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.12)" },
    QUEUED: { color: "#00e5ff", bg: "rgba(0,229,255,0.08)", border: "rgba(0,229,255,0.4)" },
    SENT: { color: "#00ff66", bg: "rgba(0,255,102,0.08)", border: "rgba(0,255,102,0.4)" },
    FAILED: { color: "#ff3333", bg: "rgba(255,51,51,0.1)", border: "rgba(255,51,51,0.4)" },
  };
  const c = map[s] || map.NOT_SENT;
  const canResend = order.status !== "WAITING"; // link only meaningful after START
  return (
    <div className="flex items-center gap-2" data-testid={`whatsapp-cell-${order.order_number}`}>
      <Badge
        className="rounded-sm border font-mono text-[10px] tracking-[0.15em]"
        style={{ color: c.color, background: c.bg, borderColor: c.border }}
        data-testid={`whatsapp-status-${s.toLowerCase()}`}
      >
        {s}
      </Badge>
      {order.whatsapp_provider && (
        <span className="text-[10px] text-white/40 font-mono">{order.whatsapp_provider}</span>
      )}
      {canResend && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onResend}
          className="h-7 w-7 text-white/50 hover:text-white"
          title={s === "FAILED" ? "Retry send" : "Resend"}
          data-testid={`whatsapp-resend-${order.order_number}`}
        >
          <Send size={13} />
        </Button>
      )}
    </div>
  );
}
